import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { catchError, finalize, scan, tap } from 'rxjs/operators';
import { environment } from '@morningstar/shared/environment';
import { KeyValuePair } from '@morningstar/shared/models';
import { BaseService } from './base-service';

export abstract class StoreService extends BaseService {

  private state = new BehaviorSubject<any>({});
  state$ = this.state.asObservable().pipe(
    scan((accumulator: {}, value: {}) => Object.assign(accumulator, value), {})
  );

  constructor(protected httpClient: HttpClient,
              @Inject('API_BASE_URL') protected baseUrl: string,
              protected snackbar: MatSnackBar) {
    super(snackbar);
  }

  pushState(selector: string, value: any) {

    // This is strictly opt-out rip my console
    if (!environment.production) {
      this.logSelectorAndValue(selector, value);
    }

    this.state.next({[selector]: value});
  }

  /**
   * Push multiple pieces of state in one call to update state
   * @param value Object containing key values pairs with selectors as keys
   */
  pushStates(value: {}) {
    if (!environment.production) {
      Object.keys(value).forEach(k => this.logSelectorAndValue(k, value[k]));
    }

    this.state.next(value);
  }

  logSelectorAndValue(selector: string, value: any) {
    console.log(`[${selector.toUpperCase()}]: PushState`, value);
  }

  read<T>(selector: string, endpoint: string, description?: string, params?: KeyValuePair[]): Observable<T> {
    endpoint = this.replaceParams(endpoint, params);

    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.get<T>(url).pipe(
      tap(data => this.pushState(selector, data)),
      catchError(error => {
        if (error.status === 400) {
          this.setSnackbarMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error retrieving ${description || selector}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  readList<T>(selector: string, endpoint: string, description?: string, params?: KeyValuePair[]): Observable<T[]> {

    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.get<T[]>(url).pipe(
      tap(data => this.pushState(selector, data)),
      catchError(error => {
        this.setSnackbarMessage(`Error retrieving ${description || selector}`);
        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  retrieveFile(endpoint: string, description: string, params?: KeyValuePair[]): Observable<any> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.get(url, { responseType: 'blob' as 'json' }).pipe(
      tap(_ => {
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          const reader: FileReader = new FileReader();

          reader.onloadend = (e) => {
            this.setErrorMessage(reader.result.toString());
          };

          reader.readAsText(error.error);
        } else {
          this.setSnackbarMessage(`Error retrieving ${description}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  create<T, R = void>(datum: T, endpoint: string, description: string, params?: KeyValuePair[],
                      customMessage?: string, customErrorMessage?: string): Observable<R> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.post<R>(url, datum).pipe(
      tap(_ => {
        if (customMessage || description) {
          this.setSnackbarMessage(`${customMessage || ('Saved ' + description)}`);
        }
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`${customErrorMessage || ('Error saving ' + description)}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  createAndRetrieveFile<T, R = void>(datum: T, endpoint: string, description: string, params?: KeyValuePair[], customMessage?: string, showMessage: boolean = true): Observable<R> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.post<R>(url, datum, { responseType: 'blob' as 'json' }).pipe(
      tap(_ => {
        if(showMessage){
          this.setSnackbarMessage(`${customMessage || ('Saved ' + description)}`);
        }
        
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          const reader: FileReader = new FileReader();

          reader.onloadend = (e) => {
            this.setErrorMessage(reader.result.toString());
          };

          reader.readAsText(error.error);
        } else {
          this.setSnackbarMessage(`Error saving ${description}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  put<T, R = void>(datum: T, endpoint: string, description?: string, params?: KeyValuePair[]): Observable<R> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.put<R>(url, datum).pipe(
      tap(_ => {
        if (description){
          this.setSnackbarMessage(`Saved ${description}`);
        }

        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          if (description){
            this.setSnackbarMessage(`Error saving ${description}`);
          } else {
            this.setSnackbarMessage('Error saving');
          }
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  putList<T, R = void>(datum: T[], endpoint: string, description: string, params?: KeyValuePair[], disableConfirmationMessage?: boolean): Observable<R> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.put<R>(url, datum).pipe(
      tap(_ => {
        if (!disableConfirmationMessage) {
          this.setSnackbarMessage(`Saved ${description}`);
        }
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error saving ${description}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  delete<R = void>(endpoint: string, description: string, params?: KeyValuePair[]): Observable<R> {
    endpoint = this.replaceParams(endpoint, params);
    const url = `${this.baseUrl}${endpoint}`;

    this.setProcessing(true);

    return this.httpClient.delete<R>(url).pipe(
      tap(_ => {
        this.setSnackbarMessage(`Removed ${description}`);
        this.setErrorMessage('');
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 400) {
          this.setErrorMessage(error.error);
        } else {
          this.setSnackbarMessage(`Error removing ${description}`);
        }

        return EMPTY;
      }),
      finalize(() => this.setProcessing(false))
    );
  }

  replaceParams(endpoint: string, params: KeyValuePair[]): string {
    if (!params) {
      return endpoint;
    }

    params.forEach(p => {
      endpoint = endpoint.replace(p.key, p.value);
    });

    return endpoint;
  }

  generateQueryString(params: any): string {
    return Object.keys(params).map(key => key + '=' + encodeURIComponent(params[key])).join('&');
  }
}
